import { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  BookOpen, Calendar, Eye, ArrowRight, Search, Tag, 
  Clock, User, ChevronLeft, Share2
} from 'lucide-react';
import { toast } from 'sonner';
import SEO from '@/components/SEO';

const API = process.env.REACT_APP_BACKEND_URL || window.location.origin;

// Blog List Page
export function BlogList() {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPosts();
  }, [selectedCategory]);

  const fetchPosts = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedCategory) params.append('category', selectedCategory);
      
      const response = await axios.get(`${API}/api/blog/posts?${params.toString()}`);
      setPosts(response.data.posts || []);
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Failed to fetch posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredPosts = posts.filter(post => 
    post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    post.excerpt.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getCategoryName = (categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    return cat ? cat.name : categoryId;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <Layout>
      <SEO 
        title="Blog - Export Tips & Market Insights"
        description="Read the latest articles on export strategies, global market trends, and B2B trade tips for exporters."
        keywords="export blog, trade tips, global markets, B2B insights, India export"
      />
      
      <div className="min-h-screen bg-slate-50">
        {/* Hero */}
        <section className="bg-gradient-to-r from-teal-600 to-teal-700 py-16 px-4 text-white text-center">
          <h1 className="text-4xl font-bold mb-4">GlobalVyapar Blog</h1>
          <p className="text-teal-100 max-w-2xl mx-auto mb-8">
            Expert insights, export tips, and market trends to help you grow your international trade business.
          </p>
          
          {/* Search */}
          <div className="max-w-md mx-auto relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
            <Input
              placeholder="Search articles..."
              className="pl-10 bg-white text-slate-900"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </section>

        {/* Categories */}
        <section className="py-6 px-4 bg-white border-b">
          <div className="max-w-6xl mx-auto flex gap-3 overflow-x-auto pb-2">
            <Button
              variant={selectedCategory === '' ? 'default' : 'outline'}
              onClick={() => setSelectedCategory('')}
              className={selectedCategory === '' ? 'bg-teal-600' : ''}
            >
              All Posts
            </Button>
            {categories.map((cat) => (
              <Button
                key={cat.id}
                variant={selectedCategory === cat.id ? 'default' : 'outline'}
                onClick={() => setSelectedCategory(cat.id)}
                className={selectedCategory === cat.id ? 'bg-teal-600' : ''}
              >
                {cat.name}
              </Button>
            ))}
          </div>
        </section>

        {/* Posts Grid */}
        <section className="py-12 px-4">
          <div className="max-w-6xl mx-auto">
            {loading ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1, 2, 3].map((i) => (
                  <Card key={i}>
                    <Skeleton className="h-48 w-full" />
                    <CardHeader>
                      <Skeleton className="h-6 w-3/4" />
                      <Skeleton className="h-4 w-1/2 mt-2" />
                    </CardHeader>
                    <CardContent>
                      <Skeleton className="h-20 w-full" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : filteredPosts.length === 0 ? (
              <div className="text-center py-16">
                <BookOpen className="h-16 w-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-slate-600 mb-2">No posts found</h3>
                <p className="text-slate-500">Try a different search or category</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredPosts.map((post) => (
                  <Card 
                    key={post.id} 
                    className="overflow-hidden hover:shadow-lg transition-shadow cursor-pointer group"
                    onClick={() => navigate(`/blog/${post.slug}`)}
                  >
                    {post.cover_image && (
                      <div className="h-48 overflow-hidden">
                        <img 
                          src={post.cover_image} 
                          alt={post.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                    )}
                    <CardHeader>
                      <Badge variant="secondary" className="w-fit mb-2">
                        {getCategoryName(post.category)}
                      </Badge>
                      <CardTitle className="text-lg group-hover:text-teal-600 transition-colors line-clamp-2">
                        {post.title}
                      </CardTitle>
                      <CardDescription className="flex items-center gap-4 text-xs">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {formatDate(post.created_at)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Eye className="h-3 w-3" />
                          {post.views || 0} views
                        </span>
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <p className="text-slate-600 text-sm line-clamp-3">{post.excerpt}</p>
                      <Button variant="link" className="p-0 mt-4 text-teal-600">
                        Read More <ArrowRight className="ml-1 h-4 w-4" />
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </section>
      </div>
    </Layout>
  );
}

// Single Blog Post Page
export function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPost();
  }, [slug]);

  const fetchPost = async () => {
    try {
      const response = await axios.get(`${API}/api/blog/posts/${slug}`);
      setPost(response.data);
    } catch (error) {
      console.error('Failed to fetch post:', error);
      toast.error('Post not found');
      navigate('/blog');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const sharePost = () => {
    if (navigator.share) {
      navigator.share({
        title: post.title,
        text: post.excerpt,
        url: window.location.href
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success('Link copied to clipboard!');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto px-4 py-12">
          <Skeleton className="h-8 w-3/4 mb-4" />
          <Skeleton className="h-4 w-1/2 mb-8" />
          <Skeleton className="h-64 w-full mb-8" />
          <div className="space-y-4">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
          </div>
        </div>
      </Layout>
    );
  }

  if (!post) return null;

  return (
    <Layout>
      <SEO 
        title={post.title}
        description={post.excerpt}
        keywords={post.tags?.join(', ')}
        type="article"
      />
      
      <article className="min-h-screen bg-white">
        {/* Back Button */}
        <div className="max-w-4xl mx-auto px-4 pt-6">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/blog')}
            className="mb-4"
          >
            <ChevronLeft className="h-4 w-4 mr-1" /> Back to Blog
          </Button>
        </div>

        {/* Header */}
        <header className="max-w-4xl mx-auto px-4 py-8">
          <Badge variant="secondary" className="mb-4">{post.category}</Badge>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
            {post.title}
          </h1>
          
          <div className="flex items-center gap-6 text-slate-500 text-sm mb-6">
            <span className="flex items-center gap-2">
              <User className="h-4 w-4" />
              {post.author}
            </span>
            <span className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              {formatDate(post.created_at)}
            </span>
            <span className="flex items-center gap-2">
              <Eye className="h-4 w-4" />
              {post.views || 0} views
            </span>
            <Button variant="ghost" size="sm" onClick={sharePost}>
              <Share2 className="h-4 w-4 mr-1" /> Share
            </Button>
          </div>

          {post.cover_image && (
            <img 
              src={post.cover_image} 
              alt={post.title}
              className="w-full h-64 md:h-96 object-cover rounded-lg"
            />
          )}
        </header>

        {/* Content */}
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div 
            className="prose prose-lg max-w-none prose-headings:text-slate-900 prose-p:text-slate-600 prose-a:text-teal-600"
            dangerouslySetInnerHTML={{ 
              __html: post.content
                .replace(/^# /gm, '<h1 class="text-3xl font-bold mt-8 mb-4">')
                .replace(/^## /gm, '<h2 class="text-2xl font-bold mt-6 mb-3">')
                .replace(/^### /gm, '<h3 class="text-xl font-semibold mt-4 mb-2">')
                .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
                .replace(/\*([^*]+)\*/g, '<em>$1</em>')
                .replace(/^- /gm, '<li class="ml-4">')
                .replace(/\n\n/g, '</p><p class="mb-4">')
                .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-teal-600 hover:underline">$1</a>')
                .replace(/^---$/gm, '<hr class="my-8 border-slate-200">')
            }}
          />

          {/* Tags */}
          {post.tags && post.tags.length > 0 && (
            <div className="mt-8 pt-8 border-t">
              <div className="flex items-center gap-2 flex-wrap">
                <Tag className="h-4 w-4 text-slate-400" />
                {post.tags.map((tag, i) => (
                  <Badge key={i} variant="outline">{tag}</Badge>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* CTA */}
        <section className="bg-teal-50 py-12 px-4 mt-12">
          <div className="max-w-2xl mx-auto text-center">
            <h3 className="text-2xl font-bold text-slate-900 mb-4">
              Ready to Grow Your Export Business?
            </h3>
            <p className="text-slate-600 mb-6">
              Join thousands of exporters using AI to find global buyers.
            </p>
            <Button 
              size="lg" 
              className="bg-teal-600 hover:bg-teal-700"
              onClick={() => navigate('/auth?tab=register&role=seller')}
            >
              Start Free Today <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </section>
      </article>
    </Layout>
  );
}

export default BlogList;
